'use client'
import Image from 'next/image'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import type { FormEvent } from 'react'
import type { Settings } from 'react-slick'
import '@/styles/vendors/slickCarousel'
import { Content } from '@prismicio/client'
import { PrismicRichText } from '@prismicio/react'
import { sliderSettingsWithoutDot } from '@/utils/sliderSettings'
import { PrismicNextLink } from '@prismicio/next'

const WHY_EXPERT_AVATARS = [
  { src: '/images/why/avatar-1.png', bg: 'bg-primary-green-20' },
  { src: '/images/why/avatar-2.png', bg: 'bg-white' },
  { src: '/images/why/avatar-3.png', bg: 'bg-white' },
] as const

// Lazy load Slider to reduce initial bundle size
const Slider = dynamic<Settings>(() => import('react-slick'), {
  ssr: false,
  loading: () => <div className="h-64 animate-pulse rounded-md bg-gray-100" />,
})

const WhyComponent = ({ data }: { data: Content.WhySlice }) => {
  const router = useRouter()

  const handleMessageSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const message = String(formData.get('message') ?? '').trim()
    if (!message) return
    router.push(`/request-demo?message=${encodeURIComponent(message)}`)
  }

  return (
    <div className="relative z-[1] bg-primary-ink-100 py-[80px] before:absolute before:top-[120px] before:-z-[1] before:hidden before:h-full before:w-full before:bg-[url('/images/symbol.svg')] before:bg-no-repeat before:content-[''] lg:py-[120px] before:lg:-left-20 before:lg:block before:xl:-left-12">
      <div className="container">
        <div className="lg:pl-[152px]">
          <div className="[&>h2>em]:block [&>h2>em]:not-italic [&>h2>em]:text-white [&>h2>em]:lg:-mt-3 [&>h2]:text-primary-green-85">
            <PrismicRichText field={data.primary.title} />
          </div>

          <div className="[&>p]:mb-6 [&>p]:mt-10 [&>p]:max-w-md [&>p]:text-white/60">
            <PrismicRichText field={data.primary.description} />
          </div>

          <PrismicNextLink field={data.primary.btn_link} className="btn btn-white btn-default btn-interaction">
            {data.primary.btn_label}
            <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
          </PrismicNextLink>

          <div className="mt-[120px] grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="hidden lg:grid lg:grid-rows-3 lg:gap-8">
              {data.items.map((item, index) => {
                const key = `i-${index}`
                return (
                  <div
                    className="rounded-md border border-primary-white-10 bg-primary-white-10 px-6 py-8 shadow-dark"
                    key={key}>
                    <span className="text-sm font-semibold text-white">{item.tag}</span>
                    <PrismicRichText
                      field={item.description}
                      components={{
                        paragraph: ({ children }) => <p className="!mb-0 !mt-2 text-[22px] !text-white">{children}</p>,
                      }}
                    />
                  </div>
                )
              })}
            </div>

            <div className="flex flex-col justify-center rounded-md border border-primary-white-10 bg-primary-white-10 px-4 py-5 shadow-dark lg:pl-[60px] lg:pr-8">
              <div className="mb-4 w-full max-w-[355px] rounded-md border border-primary-white-10 bg-primary-ink-100/60 px-6 py-6 shadow-dark">
                <div className="flex" role="presentation">
                  {WHY_EXPERT_AVATARS.map((avatar, index) => (
                    <div
                      key={avatar.src}
                      className={`relative h-12 w-12 overflow-hidden rounded-full border-2 border-primary-ink-100 ${avatar.bg} ${index > 0 ? '-ml-3' : ''} ${index === 0 ? 'z-[3]' : index === 1 ? 'z-[2]' : 'z-[1]'}`}>
                      <Image
                        src={avatar.src}
                        alt=""
                        width={48}
                        height={48}
                        className="h-full w-full object-cover object-top"
                        aria-hidden="true"
                      />
                    </div>
                  ))}
                </div>

                <p className="mb-0 mt-5 text-[22px] font-normal leading-[130%] text-white">
                  Get in touch with an expert
                </p>
                <p className="mb-0 mt-1 text-base font-normal text-white/60">Have a question?</p>

                <Link
                  href="/request-demo"
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-secondary-blue-10 px-4 py-2.5 text-sm font-normal text-primary-ink-100 transition-transform hover:scale-105">
                  <Image
                    src="/images/why/chat-icon.png"
                    alt=""
                    width={20}
                    height={20}
                    className="h-5 w-5 brightness-0"
                    aria-hidden="true"
                  />
                  Chat Now
                </Link>
              </div>

              <div className="min-w-full self-end rounded-md border border-primary-white-10 bg-primary-ink-100/60 p-4 shadow-dark lg:min-w-[280px]">
                <PrismicRichText
                  field={data.primary.help_you_text}
                  components={{
                    paragraph: ({ children }) => <p className="!my-0 text-sm text-white/80">{children}</p>,
                  }}
                />
                <form onSubmit={handleMessageSubmit}>
                  <label htmlFor="why-expert-message" className="sr-only">
                    Send message
                  </label>
                  <input
                    id="why-expert-message"
                    type="text"
                    name="message"
                    placeholder="Send message..."
                    className="mt-4 w-full rounded border border-primary-white-10 bg-primary-ink-100 px-2 py-2 text-sm text-white placeholder-white/60 outline-none ring-0 focus:border-primary-white-10 focus:outline-none focus:ring-0"
                  />
                </form>
              </div>
            </div>

            {/* why card mobile */}
            <div className="block lg:hidden">
              <Slider
                {...sliderSettingsWithoutDot}
                className="[&.slick-slider]:-mx-4 [&.slick-slider]:my-0 [&_.slick-slide]:px-3">
                {data.items.map((item, index) => {
                  const key = `i-${index}`
                  return (
                    <div
                      className="mt-5 min-h-[230px] rounded-md border border-primary-white-10 bg-primary-white-10 px-6 py-8 shadow-dark"
                      key={key}>
                      <span className="text-sm font-semibold text-white">{item.tag}</span>
                      <PrismicRichText
                        field={item.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="!mb-0 !mt-5 text-[22px] !text-white">{children}</p>
                          ),
                        }}
                      />
                    </div>
                  )
                })}
              </Slider>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WhyComponent
